import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X } from 'react-feather';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { getSupplierPaymentTerms } from '../../../../redux/services/suppliersService';
import {
  internalApproveCreditApplication,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getIntegrationName, openDillToast } from '../../../../utils/helpers';
import {
  CreditApplication,
  formatMoney,
  getCreditApplicationTypeLabel,
  InternalSignatureDocument,
  isCodApplicationType,
  isExternalFormApplicationType,
  RutterSubsidiary,
  SupplierPaymentTerm,
} from '@dill/dill-shared';
import { getRutterSubsidiaries } from '../../../../redux/services/rutterService';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { Plus } from 'phosphor-react';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillInput } from '../../../general/Dill/DillInput';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { getApprovalFieldOptions } from './approvalFieldOptions';
import { AppDropDown2 } from '../../../general/AppDropDown2/AppDropDown2';
import LexicalEditor from '../../../general/LexicalEditor/LexicalEditor';
import { closeMessageModal, openMessageModal } from '../../../../redux/globalSlices/genericSlice';

const EMAIL_REGEX = /[\w.+-]+@[\w-]+\.[a-z]{1,}/gi;

const dedupeEmails = (emails: (string | undefined | null)[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const email of emails) {
    const normalized = email?.toLowerCase().trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
};

const ApproveCreditAppEmailsModal = ({
  open,
  handleClose,
  creditApplication,
  mode,
  applicationHistoryId,
  coversheetChargeAccountType,
  coversheetExistingAccountNumber,
  internalSignatureDocuments = [],
}: {
  open: boolean;
  handleClose: () => void;
  creditApplication: CreditApplication;
  onSuccess: () => void;
  mode: 'NOTIFY' | 'INTERNALLY_APPROVE' | 'INTERNALLY_APPROVE_COD' | 'INTERNALLY_REJECT';
  applicationHistoryId?: string | null;
  // Branch Coversheet's "Account Type" (charge-account-type) / "Existing Account Number"
  // (charge-existing-account-number) — when the Branch Manager already marked this a "Convert
  // Cash to Charge" account, Approve Credit should default to Update Customer with that account
  // number pre-filled instead of starting blank. See the prefill effect below.
  coversheetChargeAccountType?: string;
  coversheetExistingAccountNumber?: string;
  // The application's Internal Signature Documents, for pre-filling their signers when the
  // supplier's notifyInternalSignatureSignersOnApproval is on.
  internalSignatureDocuments?: InternalSignatureDocument[];
}) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [creditAppEmails, setCreditAppEmails] = useState<string[]>([]);
  const [emailTextInputValue, setEmailTextInputValue] = useState('');
  const [emailTextInputErrorMessage, setEmailTextInputErrorMessage] = useState('');
  const [amountApproved, setAmountApproved] = useState(0);
  const [missingEmails, setMissingEmails] = useState(false);
  const [missingTerms, setMissingTerms] = useState(false);
  const [missingCustomerAccountNumber, setMissingCustomerAccountNumber] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedRejectReasons, setSelectedRejectReasons] = useState<Set<string>>(new Set());
  const [otherReasonText, setOtherReasonText] = useState('');
  const [status, setStatus] = useState<string>('');
  const [customerAccountNumber, setCustomerAccountNumber] = useState('');
  // Eclipse external forms on "update": the existing Bill-To, alongside the Ship-To above.
  const [billToAccountNumber, setBillToAccountNumber] = useState('');
  const [missingBillToAccountNumber, setMissingBillToAccountNumber] = useState(false);
  const [terms, setTerms] = useState('');
  const [customTermsText, setCustomTermsText] = useState('');
  const [approvalAdditionalFieldValues, setApprovalAdditionalFieldValues] = useState<
    { id: string; name: string; erpId?: string; value: string }[]
  >([]);
  const [customOptionValues, setCustomOptionValues] = useState<Record<string, string>>({});
  const [currencyDisplayValues, setCurrencyDisplayValues] = useState<Record<string, string>>({});
  const [internalEmailList, setInternalEmailList] = useState<string[]>([]);
  const [customerEmailList, setCustomerEmailList] = useState<string[]>([]);
  const [internalEmailInput, setInternalEmailInput] = useState('');
  const [internalEmailInputError, setInternalEmailInputError] = useState('');
  const [customerEmailInput, setCustomerEmailInput] = useState('');
  const [customerEmailInputError, setCustomerEmailInputError] = useState('');
  const [useOneOffOverride, setUseOneOffOverride] = useState(false);
  const [overrideSubject, setOverrideSubject] = useState('');
  const [overrideMessageHtml, setOverrideMessageHtml] = useState('');
  // NetSuite requires a subsidiary on every customer it creates and won't let it be changed later,
  // so the approver picks one when the supplier's approval action is 'create'.
  const [subsidiaries, setSubsidiaries] = useState<RutterSubsidiary[]>([]);
  const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState('');
  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false);
  const [missingSubsidiary, setMissingSubsidiary] = useState(false);
  // The options behind the "Terms" dropdown. The backend decides where they come from — an ERP's own
  // terms table where one is synced, otherwise the terms configured in Dill — so this component
  // never branches on integration type.
  const [paymentTerms, setPaymentTerms] = useState<SupplierPaymentTerm[]>([]);
  const [loadingPaymentTerms, setLoadingPaymentTerms] = useState(false);
  // Guards against a fetch started for a previously selected supplier writing its results
  // into the now differently-scoped state.
  const activeFetchSupplierIdRef = useRef<string | null>(null);
  // Frozen at the moment the override is turned on so LexicalEditor (uncontrolled) seeds once
  // from the template and doesn't fight the user's typing on every keystroke afterwards.
  const [overrideMessageSeed, setOverrideMessageSeed] = useState('');

  const formatWithCommas = (raw: string): string => {
    const clean = raw.replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.slice(0, 2).join('.');
  };

  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );

  const isQBOOrP21OrDMSIOrInforSX = useMemo(
    () =>
      currentSupplier?.integrationType === 'QBO' ||
      currentSupplier?.integrationType === 'P21' ||
      currentSupplier?.integrationType === 'AGILITY_DMSI' ||
      currentSupplier?.integrationType === 'INFOR_DIST_SX',
    [currentSupplier]
  );

  const isRutterNetsuite = useMemo(
    () => currentSupplier?.integrationType === 'RUTTER_NETSUITE',
    [currentSupplier]
  );

  const isDMSI = useMemo(
    () => currentSupplier?.integrationType === 'AGILITY_DMSI',
    [currentSupplier]
  );

  const isInforSX = useMemo(
    () => currentSupplier?.integrationType === 'INFOR_DIST_SX',
    [currentSupplier]
  );

  // Eclipse: "create" makes a Bill-To plus a Ship-To with the approved terms and credit limit;
  // "update" sets an existing Ship-To's terms and credit limit (its number goes in the customer
  // number input).
  const isEclipse = useMemo(
    () => currentSupplier?.integrationType === 'ECLIPSE',
    [currentSupplier]
  );

  // Integrations that write the customer to the ERP as part of approval, so the applicant is
  // notified in the same step instead of a follow-up one. Gates the one-step copy and email
  // handling; the ERP-specific input for picking the customer stays keyed off the integration.
  const hasErpCustomerIntegration = useMemo(
    () => isQBOOrP21OrDMSIOrInforSX || isRutterNetsuite || isEclipse,
    [isQBOOrP21OrDMSIOrInforSX, isRutterNetsuite, isEclipse]
  );

  const skipCustomerActionOnCod = useMemo(
    () => !!currentSupplier?.creditApplicationSetting?.skipCustomerActionOnCod,
    [currentSupplier]
  );

  const defaultCustomerOnApprovalAction = useMemo(() => {
    if (mode === 'INTERNALLY_APPROVE_COD' && skipCustomerActionOnCod) return 'none';
    return currentSupplier?.creditApplicationSetting?.customerOnApprovalAction ?? 'none';
  }, [currentSupplier, mode, skipCustomerActionOnCod]);

  const [customerActionOverride, setCustomerActionOverride] = useState<'update' | 'create' | null>(
    null
  );

  useEffect(() => {
    if (!open) {
      setCustomerActionOverride(null);
    }
  }, [open]);

  const customerOnApprovalAction = useMemo(
    () => customerActionOverride ?? defaultCustomerOnApprovalAction,
    [customerActionOverride, defaultCustomerOnApprovalAction]
  );

  const canOverrideCustomerAction = useMemo(
    () =>
      hasErpCustomerIntegration &&
      (defaultCustomerOnApprovalAction === 'update' ||
        defaultCustomerOnApprovalAction === 'create'),
    [hasErpCustomerIntegration, defaultCustomerOnApprovalAction]
  );

  // Approve Credit only (not COD/reject) — the Branch Coversheet already told us this account was
  // converted from Cash to Charge, i.e. it already exists in P21, so default straight to Update
  // Customer with its account number pre-filled rather than making the reviewer look it up and
  // re-type it. Still just a default: the reviewer can change the action/account number dropdown
  // and input same as always.
  useEffect(() => {
    if (
      open &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD') &&
      canOverrideCustomerAction &&
      coversheetChargeAccountType === 'Convert Cash to Charge' &&
      coversheetExistingAccountNumber
    ) {
      setCustomerActionOverride('update');
      setCustomerAccountNumber(coversheetExistingAccountNumber);
    }
  }, [
    open,
    mode,
    canOverrideCustomerAction,
    coversheetChargeAccountType,
    coversheetExistingAccountNumber,
  ]);

  const isP21 = useMemo(() => currentSupplier?.integrationType === 'P21', [currentSupplier]);

  // Eclipse: the Bill-To and a COD Ship-To were already created once the internal signatures were
  // complete (createErpCustomerOnInternalSignaturesComplete), so approving only sets that Ship-To's
  // terms and credit limit — the backend's "create" does that when the Ship-To already exists.
  const eclipseShipToCreated =
    isEclipse && canOverrideCustomerAction && !!creditApplication.shipToAccountNumber;
  // Eclipse external forms (Job Info, New Ship-To, ...) are for customers that already exist, so
  // they default to updating an existing Bill-To/Ship-To, pre-filled with any already saved.
  const isEclipseExternalForm =
    isEclipse && isExternalFormApplicationType(creditApplication?.applicationType);
  // The form's template can fix each step: e.g. New Ship-To / Job Information leave the Bill-To
  // alone and create a new Ship-To under it ("create" — only the Bill-To number is asked for);
  // Reactivate updates both ("update" — Bill-To and Ship-To numbers).
  const eclipseFormTemplate = isEclipseExternalForm
    ? creditApplicationTemplates.find((t) => t.id === creditApplication.templateId)
    : undefined;
  const eclipseFormBillToAction = eclipseFormTemplate?.internalFormBillToAction ?? null;
  const eclipseFormShipToAction = eclipseFormTemplate?.internalFormShipToAction ?? null;
  const isEclipseFormActionFixed = !!(eclipseFormBillToAction || eclipseFormShipToAction);
  // A new Ship-To under a Bill-To that's left alone takes the Bill-To's terms in Eclipse.
  const isEclipseShipToFollowingBillToTerms =
    eclipseFormBillToAction === 'NONE' && eclipseFormShipToAction === 'CREATE';
  const eclipseFormAction = eclipseFormShipToAction === 'CREATE' ? 'create' : 'update';
  const eclipseFormActionLabel = `${[
    eclipseFormBillToAction === 'UPDATE' ? 'Update Bill-To' : '',
    eclipseFormShipToAction === 'CREATE'
      ? 'Create Ship-To'
      : eclipseFormShipToAction === 'UPDATE'
      ? 'Update Ship-To'
      : '',
  ]
    .filter(Boolean)
    .join(' and ')} in Eclipse`;
  const showEclipseBillToInput =
    isEclipseExternalForm &&
    (customerOnApprovalAction === 'update' ||
      (customerOnApprovalAction === 'create' && eclipseFormShipToAction === 'CREATE')) &&
    (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD');
  useEffect(() => {
    if (open && isEclipseExternalForm && canOverrideCustomerAction) {
      setCustomerActionOverride(isEclipseFormActionFixed ? eclipseFormAction : 'update');
      setCustomerAccountNumber(creditApplication.shipToAccountNumber ?? '');
      setBillToAccountNumber(creditApplication.customerAccountNumber ?? '');
    }
  }, [
    open,
    isEclipseExternalForm,
    canOverrideCustomerAction,
    isEclipseFormActionFixed,
    eclipseFormAction,
    creditApplication.shipToAccountNumber,
    creditApplication.customerAccountNumber,
  ]);
  useEffect(() => {
    if (open && eclipseShipToCreated && !isEclipseExternalForm) {
      setCustomerActionOverride(defaultCustomerOnApprovalAction === 'create' ? null : 'create');
      setCustomerAccountNumber('');
    }
  }, [open, eclipseShipToCreated, isEclipseExternalForm, defaultCustomerOnApprovalAction]);

  const defaultPreserveExistingAccountDetails = useMemo(
    () => currentSupplier?.creditApplicationSetting?.preserveExistingAccountDetails ?? false,
    [currentSupplier]
  );

  const [preserveAccountDetailsOverride, setPreserveAccountDetailsOverride] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    if (!open) {
      setPreserveAccountDetailsOverride(null);
    }
  }, [open]);

  const preserveExistingAccountDetails = useMemo(
    () => preserveAccountDetailsOverride ?? defaultPreserveExistingAccountDetails,
    [preserveAccountDetailsOverride, defaultPreserveExistingAccountDetails]
  );

  const canOverridePreserveAccountDetails = useMemo(
    () => isP21 && customerOnApprovalAction === 'update',
    [isP21, customerOnApprovalAction]
  );

  const showSplitEmailLists = useMemo(
    () =>
      hasErpCustomerIntegration &&
      (customerOnApprovalAction === 'update' || customerOnApprovalAction === 'create') &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD'),
    [hasErpCustomerIntegration, customerOnApprovalAction, mode]
  );

  // NetSuite requires a subsidiary on every customer it creates and won't let it be reassigned
  // afterwards, so it is only picked — and only sent — on 'create'.
  const showNetsuiteSubsidiaryDropdown = useMemo(
    () =>
      isRutterNetsuite &&
      customerOnApprovalAction === 'create' &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD'),
    [isRutterNetsuite, customerOnApprovalAction, mode]
  );

  // Agility DMSi requires the CustomerID up front on create — unlike QBO/P21/NetSuite, which
  // hand back an id after creating the customer. So DMSi alone also collects a required
  // account number on 'create', used as that CustomerID.
  const showDmsiAccountNumberInput = useMemo(
    () =>
      isDMSI &&
      customerOnApprovalAction === 'create' &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD'),
    [isDMSI, customerOnApprovalAction, mode]
  );

  // Infor SX can auto-assign the customer number on create, but a supplier that numbers customers
  // by hand needs to supply one — so Infor gets an optional account number on 'create'.
  const showInforSXAccountNumberInput = useMemo(
    () =>
      isInforSX &&
      customerOnApprovalAction === 'create' &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD'),
    [isInforSX, customerOnApprovalAction, mode]
  );

  // Only the term *name* is submitted; the backend resolves it back to the ERP's own term id when it
  // writes the customer.
  const shouldFetchPaymentTerms = useMemo(
    () => mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD',
    [mode]
  );

  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);

  const fetchSubsidiaries = async () => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoadingSubsidiaries(true);
    try {
      const results = await dispatch(getRutterSubsidiaries({ supplierId: fetchSupplierId }));
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        return;
      }
      const fetchedSubsidiaries = ((results.payload as any)?.data ?? []) as RutterSubsidiary[];
      setSubsidiaries(fetchedSubsidiaries);
      // With a single subsidiary there is nothing to choose, so pick it for the approver.
      if (fetchedSubsidiaries.length === 1) {
        setSelectedSubsidiaryId(fetchedSubsidiaries[0].id ?? '');
      }
    } finally {
      if (activeFetchSupplierIdRef.current === fetchSupplierId) {
        setLoadingSubsidiaries(false);
      }
    }
  };

  useEffect(() => {
    if (open && showNetsuiteSubsidiaryDropdown) {
      fetchSubsidiaries();
    }
  }, [open, showNetsuiteSubsidiaryDropdown]);

  const fetchPaymentTerms = async () => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoadingPaymentTerms(true);
    try {
      const results = await dispatch(getSupplierPaymentTerms({ supplierId: fetchSupplierId }));
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        return;
      }
      const fetchedPaymentTerms = ((results.payload as any)?.data ?? []) as SupplierPaymentTerm[];
      setPaymentTerms(fetchedPaymentTerms);
    } finally {
      if (activeFetchSupplierIdRef.current === fetchSupplierId) {
        setLoadingPaymentTerms(false);
      }
    }
  };

  useEffect(() => {
    if (open && shouldFetchPaymentTerms) {
      fetchPaymentTerms();
    }
  }, [open, shouldFetchPaymentTerms]);

  // If a term was picked from the fallback list before the fetched options arrived and replaced it,
  // drop the selection — otherwise the dropdown shows a term that is no longer selectable.
  useEffect(() => {
    if (paymentTerms.length === 0 || !terms) {
      return;
    }
    if (!paymentTerms.some((paymentTerm) => paymentTerm.name === terms)) {
      setTerms('');
    }
  }, [paymentTerms]);

  const handleCloseModal = () => {
    setLoading(false);
    setAmountApproved(0);
    setCreditAppEmails([]);
    setReason('');
    setSelectedRejectReasons(new Set());
    setOtherReasonText('');
    setCustomerAccountNumber('');
    setBillToAccountNumber('');
    setTerms('');
    setCustomTermsText('');
    setMissingTerms(false);
    setMissingCustomerAccountNumber(false);
    setMissingBillToAccountNumber(false);
    setStatus('');
    setEmailTextInputValue('');
    setEmailTextInputErrorMessage('');
    setApprovalAdditionalFieldValues([]);
    setCustomOptionValues({});
    setCurrencyDisplayValues({});
    setApprovalAdditionalFieldValues([]);
    setCustomOptionValues({});
    setCurrencyDisplayValues({});
    setInternalEmailList([]);
    setCustomerEmailList([]);
    setInternalEmailInput('');
    setInternalEmailInputError('');
    setCustomerEmailInput('');
    setCustomerEmailInputError('');
    setUseOneOffOverride(false);
    setOverrideSubject('');
    setOverrideMessageHtml('');
    setOverrideMessageSeed('');
    setSubsidiaries([]);
    setSelectedSubsidiaryId('');
    setMissingSubsidiary(false);
    setPaymentTerms([]);
    handleClose();
  };

  const notifyTemplate = useMemo(() => {
    if (creditApplication.templateId) {
      return creditApplicationTemplates.find((t) => t.id === creditApplication.templateId);
    }
    // Manual-upload applications aren't linked to a template, so fall back to the first
    // supplier template matching the application's type to source approvalAdditionalFields.
    return creditApplicationTemplates.find(
      (t) =>
        t.supplierId === currentSupplier?.id &&
        t.applicationType === creditApplication.applicationType
    );
  }, [
    creditApplicationTemplates,
    creditApplication.templateId,
    creditApplication.applicationType,
    currentSupplier?.id,
  ]);

  // External forms are on credit terms, so they're approved like credit applications - only how
  // the application is named differs (the form's template name instead of "Credit Application").
  const isCreditApp = !isCodApplicationType(creditApplication?.applicationType);
  const applicationTypeTitle = getCreditApplicationTypeLabel(creditApplication?.applicationType, {
    long: true,
    formName: notifyTemplate?.name,
  });
  const applicationTypeName = isExternalFormApplicationType(creditApplication?.applicationType)
    ? applicationTypeTitle
    : isCreditApp
    ? 'credit application'
    : 'COD application';

  // The template's saved subject/message for whichever decision this modal is currently
  // making — used to preload the one-off override so the user is editing a starting point
  // rather than a blank message.
  const templateOverrideDefaults = useMemo(() => {
    if (mode === 'INTERNALLY_APPROVE_COD') {
      return {
        subject: notifyTemplate?.approveCODSubjectLine ?? '',
        message: notifyTemplate?.approveCODMessage ?? '',
      };
    }
    if (mode === 'INTERNALLY_REJECT') {
      return {
        subject: notifyTemplate?.rejectSubjectLine ?? '',
        message: notifyTemplate?.rejectCODMessage ?? '',
      };
    }
    return {
      subject: notifyTemplate?.approvalSubjectLine ?? '',
      message: notifyTemplate?.approvalMessage ?? '',
    };
  }, [mode, notifyTemplate]);

  // Seed the field defaults into state when the modal opens. The inputs only *display*
  // `field.defaultValue` as a fallback — without seeding, a defaulted value (including a
  // legitimate 0) never lands in `approvalAdditionalFieldValues` until the user re-types it,
  // so it's missing from the submit payload. We only add entries that aren't already present,
  // so any user edits are preserved.
  useEffect(() => {
    if (!open) {
      return;
    }
    const fields = notifyTemplate?.approvalAdditionalFields ?? [];
    const withDefaults = fields.filter(
      (f) => f.defaultValue !== undefined && f.defaultValue !== null && `${f.defaultValue}` !== ''
    );
    if (withDefaults.length === 0) {
      return;
    }
    setApprovalAdditionalFieldValues((prev) => {
      const missing = withDefaults
        .filter((f) => !prev.some((e) => e.id === f.id))
        .map((f) => ({
          id: f.id,
          name: f.label,
          erpId: f.erpId,
          value: `${f.defaultValue}`,
        }));
      return missing.length > 0 ? [...prev, ...missing] : prev;
    });
  }, [open, notifyTemplate]);

  const activeRejectReasons = useMemo(() => {
    const reasons = notifyTemplate?.approveCODReasons ?? [];
    return [...reasons].sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return 0;
    });
  }, [notifyTemplate]);

  // `terms` now lives on the supplierAdditionalDetails subcollection rather than the Supplier doc
  // itself (see supplierAdditionalDetailsSchema.ts), so `paymentTerms` - fetched via
  // getSupplierPaymentTerms, which already resolves it server-side - is the only source; there's
  // no longer a `currentSupplier.terms` to fall back to while the request is in flight.
  const supplierTerms = paymentTerms;

  const resolvedTerms =
    mode === 'INTERNALLY_APPROVE_COD' || creditApplication.applicationType === 'CASH_APPLICATION'
      ? 'COD'
      : terms === '__custom__'
      ? customTermsText.trim()
      : terms.trim();

  const amountOfCreditRequested = useMemo(() => {
    const amount: number =
      creditApplication?.fieldValues?.find((field) => field.id.includes('amount-of'))?.value ?? 0;
    return amount;
  }, [open, creditApplication]);

  const amountOfCreditApproved = useMemo(() => {
    let amount: number = creditApplication?.amountAproved ?? 0;
    if (
      currentSupplier &&
      currentSupplier.domainId === 'auer' &&
      (mode === 'INTERNALLY_APPROVE_COD' ||
        creditApplication.applicationType === 'CASH_APPLICATION')
    ) {
      amount = 0.01;
    }
    return amount;
  }, [open, creditApplication]);

  useEffect(() => {
    if (open) {
      if (
        currentSupplier &&
        currentSupplier.domainId === 'auer' &&
        (mode === 'INTERNALLY_APPROVE_COD' ||
          creditApplication.applicationType === 'CASH_APPLICATION')
      ) {
        setAmountApproved(0.01);
      } else {
        setAmountApproved(amountOfCreditRequested);
      }
    }
  }, [open, amountOfCreditRequested]);

  useEffect(() => {
    if (open && (mode === 'NOTIFY' || mode === 'INTERNALLY_REJECT')) {
      const salesIds = creditApplication?.assignedSalesRep ?? [];
      const salesReps = supplierBranches.flatMap((branch) => branch?.salesRep ?? []);
      const senderEmail = creditApplication?.senderEmail ? [creditApplication?.senderEmail] : [];
      const creditEmails = currentSupplier?.creditApplicationSetting?.creditApplicationEmails ?? [];
      const salesRepEmails =
        currentSupplier?.creditApplicationSetting?.notifySalesRepOnApproval !== false
          ? salesReps
              .filter((salesRep) => salesIds.includes(salesRep?.id ?? ''))
              .map((salesRep) => salesRep.email)
              .filter((email): email is string => Boolean(email))
          : [];

      setCreditAppEmails((prev) =>
        dedupeEmails([...prev, ...senderEmail, ...creditEmails, ...salesRepEmails])
      );
    }

    if (open && (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD')) {
      const internalEmails = currentSupplier?.creditApplicationSetting?.internalEmails ?? [];
      setCreditAppEmails((prev) => dedupeEmails([...prev, ...internalEmails]));
    }
  }, [open]);

  useEffect(() => {
    if (open && mode.includes('INTERNALLY_APPROVE') && !showSplitEmailLists) {
      const internalEmails = (currentSupplier?.creditApplicationSetting?.internalEmails ??
        []) as string[];
      setCreditAppEmails(dedupeEmails(internalEmails));
    }
  }, [open, mode, showSplitEmailLists, currentSupplier?.creditApplicationSetting?.internalEmails]);

  useEffect(() => {
    if (
      open &&
      hasErpCustomerIntegration &&
      (customerOnApprovalAction === 'update' || customerOnApprovalAction === 'create') &&
      (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD')
    ) {
      const internalEmails = (currentSupplier?.creditApplicationSetting?.internalEmails ??
        []) as string[];
      setInternalEmailList(dedupeEmails(internalEmails));
      const senderEmail = creditApplication?.senderEmail ? [creditApplication.senderEmail] : [];
      const salesIds = creditApplication?.assignedSalesRep ?? [];
      const salesReps = supplierBranches.flatMap((branch) => branch?.salesRep ?? []);
      const salesRepEmails =
        currentSupplier?.creditApplicationSetting?.notifySalesRepOnApproval !== false
          ? salesReps
              .filter((salesRep: any) => salesIds.includes(salesRep?.id ?? ''))
              .map((salesRep: any) => salesRep.email)
              .filter((email: any): email is string => Boolean(email))
          : [];
      setCustomerEmailList(dedupeEmails([...senderEmail, ...salesRepEmails]));
    }
  }, [open]);

  const notifySignersOnApproval =
    !!currentSupplier?.creditApplicationSetting?.notifyInternalSignatureSignersOnApproval;

  // Everyone who signed one of the application's Internal Signature Documents — the signing
  // candidate whose userId is the slot's signedByUserId.
  const signerEmails = useMemo(() => {
    if (!notifySignersOnApproval) return [];
    return dedupeEmails(
      internalSignatureDocuments.flatMap((doc) =>
        (doc.signers ?? [])
          .filter((signer) => !!signer.signature && !!signer.signedByUserId)
          .map(
            (signer) =>
              signer.candidates.find((candidate) => candidate.userId === signer.signedByUserId)
                ?.email
          )
      )
    );
  }, [internalSignatureDocuments, notifySignersOnApproval]);

  // The documents can still be loading when the modal opens, so the signers are merged in once
  // they resolve rather than in the open effects above — and only once per open, so a signer the
  // approver removed doesn't reappear. Lands in the same list as the sales reps.
  const signerEmailsAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      signerEmailsAppliedRef.current = false;
      return;
    }
    if (signerEmailsAppliedRef.current || signerEmails.length === 0) return;
    if (mode === 'NOTIFY' || mode === 'INTERNALLY_REJECT') {
      setCreditAppEmails((prev) => dedupeEmails([...prev, ...signerEmails]));
    } else if (showSplitEmailLists) {
      setCustomerEmailList((prev) => dedupeEmails([...prev, ...signerEmails]));
    } else {
      return;
    }
    signerEmailsAppliedRef.current = true;
  }, [open, signerEmails]);

  const buildRejectReasonsHtml = () => {
    const reasons = [...selectedRejectReasons]
      .map((r) => {
        if (r === 'Other')
          return otherReasonText.trim() ? `Other: ${otherReasonText.trim()}` : null;
        return r;
      })
      .filter(Boolean) as string[];
    if (reasons.length === 0) return '';
    return `<ul style="margin:4px 0;padding-left:20px;">${reasons
      .map((r) => `<li>${r}</li>`)
      .join('')}</ul>`;
  };

  const handleInternalAprove = async (emails: string[], customerNotifyEmails?: string[]) => {
    setLoading(true);
    let additionalData = {};
    if (mode === 'INTERNALLY_APPROVE_COD' && isCreditApp) {
      const reasonsHtml = buildRejectReasonsHtml();
      if (reasonsHtml) {
        additionalData = { ...additionalData, rejectReason: reasonsHtml };
      }
    } else if (reason.trim()) {
      additionalData = { ...additionalData, rejectReason: reason.trim() };
    }
    if (mode === 'INTERNALLY_APPROVE' && isCreditApp) {
      additionalData = { ...additionalData, amountAproved: amountApproved };
    }

    if (
      mode === 'INTERNALLY_APPROVE_COD' ||
      creditApplication.applicationType === 'CASH_APPLICATION'
    ) {
      additionalData = { ...additionalData, amountAproved: amountApproved };
    }
    if (resolvedTerms) {
      additionalData = { ...additionalData, terms: resolvedTerms };
    }
    const resolvedFieldValues = approvalAdditionalFieldValues.map((entry) => {
      const fieldDef = notifyTemplate?.approvalAdditionalFields?.find((f) => f.id === entry.id);
      if (fieldDef?.allowCustomOption && entry.value === '__custom__') {
        return { ...entry, value: customOptionValues[entry.id] ?? '' };
      }
      return entry;
    });
    if (resolvedFieldValues.length > 0) {
      additionalData = { ...additionalData, approvalAdditionalFieldValues: resolvedFieldValues };
    }
    // Pass customer account number when action is 'update', or when creating a DMSi/Infor SX
    // customer — Agility needs the CustomerID supplied up front and Infor SX optionally takes one,
    // unlike QBO/P21/NetSuite which assign one.
    if (
      (customerOnApprovalAction === 'update' ||
        (customerOnApprovalAction === 'create' && (isDMSI || isInforSX))) &&
      customerAccountNumber.trim()
    ) {
      additionalData = { ...additionalData, customerAccountNumber: customerAccountNumber.trim() };
    }
    if (showEclipseBillToInput && billToAccountNumber.trim()) {
      additionalData = { ...additionalData, billToAccountNumber: billToAccountNumber.trim() };
    }
    // The subsidiary the customer gets created under; NetSuite-only and create-only.
    if (showNetsuiteSubsidiaryDropdown && selectedSubsidiaryId) {
      additionalData = { ...additionalData, subsidiaryId: selectedSubsidiaryId };
    }
    if (customerOnApprovalAction === 'create' || customerOnApprovalAction === 'update') {
      additionalData = { ...additionalData, customerOnApprovalAction };
    }
    if (canOverridePreserveAccountDetails) {
      additionalData = { ...additionalData, preserveExistingAccountDetails };
    }
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Processing customer approval',
        subTitle: `This may take a few minutes. ${
          mode !== 'INTERNALLY_APPROVE_COD' ? 'Customer in your ERP is being updated.' : ''
        } Please do not refresh the page.`,
      })
    );
    const results = await dispatch(
      internalApproveCreditApplication({
        creditApplicationId: creditApplication?.id || '',
        status:
          mode === 'INTERNALLY_APPROVE_COD' ? 'INTERNALLY_APPROVED_COD' : 'INTERNALLY_APPROVED',
        supplierId: currentSupplier?.id || '',
        ...(emails.length > 0 && { emailsToNotify: emails }),
        ...(applicationHistoryId && { applicationHistoryId }),
        ...additionalData,
      })
    );

    if (!results.type.includes('fulfilled')) {
      setLoading(false);

      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: `Error approving ${applicationTypeName}`,
          subTitle: (results.payload as string) || `Failed to approve ${applicationTypeName}`,
        })
      );
      return;
    }
    dispatch(closeMessageModal());

    const payload = (results.payload as any)?.data;
    const createdCustomerNumber = payload?.customerAccountNumber;

    // For QBO/P21/NetSuite with create/update action, auto-complete step 2 (one-step flow)
    if (
      (hasErpCustomerIntegration && customerOnApprovalAction !== 'none') ||
      createdCustomerNumber
    ) {
      const accountNum =
        createdCustomerNumber ??
        (customerOnApprovalAction === 'update' ? customerAccountNumber.trim() : undefined);
      const uniqueNotifyEmails = showSplitEmailLists
        ? customerNotifyEmails ?? customerEmailList
        : [];

      const approveResult = await dispatch(
        updateSupplierCreditApplication({
          creditApplicationId: creditApplication?.id || '',
          status: 'APPROVED',
          supplierId: currentSupplier?.id || '',
          ...(accountNum && { customerAccountNumber: accountNum }),
          ...(uniqueNotifyEmails.length > 0 && { emailsToNotify: uniqueNotifyEmails }),
          ...(resolvedTerms && { terms: resolvedTerms }),
          ...(useOneOffOverride &&
            overrideMessageHtml && {
              oneOffMessageOverrideHtml: overrideMessageHtml,
              ...(overrideSubject.trim() && {
                oneOffMessageOverrideSubject: overrideSubject.trim(),
              }),
            }),
        })
      );
      setLoading(false);
      if (approveResult.type.includes('fulfilled')) {
        openDillToast({ type: 'SUCCESS', message: 'Application approved and customer notified.' });
      } else {
        openDillToast({
          type: 'ERROR',
          message: 'Approved, but failed to send customer notification.',
        });
      }
    } else {
      setLoading(false);
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully approved ${applicationTypeName}`,
      });
    }
    handleCloseModal();
  };

  const handleInternalReject = async (emails: string[]) => {
    setLoading(true);
    let additionalData = {};
    if (reason.trim()) {
      additionalData = { ...additionalData, rejectReason: reason.trim() };
    }

    // Resubmission history records are tracked on a separate CreditApplicationHistory
    // doc, which only this endpoint knows how to update - it has to run first.
    if (applicationHistoryId) {
      const historyResults = await dispatch(
        internalApproveCreditApplication({
          creditApplicationId: creditApplication?.id || '',
          status: 'REJECTED',
          supplierId: currentSupplier?.id || '',
          applicationHistoryId,
        })
      );
      if (!historyResults.type.includes('fulfilled')) {
        setLoading(false);
        openDillToast({
          type: 'ERROR',
          message: `Failed to reject ${applicationTypeName}`,
        });
        return;
      }
    }

    const results = await dispatch(
      updateSupplierCreditApplication({
        creditApplicationId: creditApplication?.id || '',
        status: 'REJECTED',
        supplierId: currentSupplier?.id || '',
        ...(emails.length > 0 && { emailsToNotify: emails }),
        ...additionalData,
        ...(useOneOffOverride &&
          overrideMessageHtml && {
            oneOffMessageOverrideHtml: overrideMessageHtml,
            ...(overrideSubject.trim() && { oneOffMessageOverrideSubject: overrideSubject.trim() }),
          }),
      })
    );
    setLoading(false);
    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully rejected ${applicationTypeName}`,
      });
      handleCloseModal();
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to reject ${applicationTypeName}`,
      });
    }
  };

  const handleAprove = async (emails: string[]) => {
    setLoading(true);
    let additionalData = {};
    if (reason.trim()) {
      additionalData = { ...additionalData, rejectReason: reason.trim() };
    }
    if (customerAccountNumber.trim()) {
      additionalData = { ...additionalData, customerAccountNumber: customerAccountNumber.trim() };
    }
    if (resolvedTerms) {
      additionalData = { ...additionalData, terms: resolvedTerms };
    }
    const results = await dispatch(
      updateSupplierCreditApplication({
        creditApplicationId: creditApplication?.id || '',
        status: creditApplication.status === 'INTERNALLY_REJECTED' ? 'REJECTED' : 'APPROVED',
        supplierId: currentSupplier?.id || '',
        emailsToNotify: emails,
        ...additionalData,
        ...(useOneOffOverride &&
          overrideMessageHtml && {
            oneOffMessageOverrideHtml: overrideMessageHtml,
            ...(overrideSubject.trim() && { oneOffMessageOverrideSubject: overrideSubject.trim() }),
          }),
      })
    );
    setLoading(false);
    const isRejection = creditApplication.status === 'INTERNALLY_REJECTED';
    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully ${isRejection ? 'rejected' : 'approved'} ${applicationTypeName}`,
      });
      handleCloseModal();
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to ${isRejection ? 'reject' : 'approve'} ${applicationTypeName}.`,
      });
    }
  };

  const title = useMemo(() => {
    if (mode === 'INTERNALLY_APPROVE') {
      const prefix =
        hasErpCustomerIntegration && customerOnApprovalAction !== 'none' ? '' : 'Internally ';
      return `${prefix}Approve ${applicationTypeTitle}`;
    }
    if (mode === 'INTERNALLY_APPROVE_COD') {
      const prefix =
        hasErpCustomerIntegration && customerOnApprovalAction !== 'none' ? '' : 'Internally ';
      return isCreditApp
        ? `${prefix}Reject ${applicationTypeTitle} and Approve COD`
        : `${prefix}Approve COD`;
    }
    if (mode === 'INTERNALLY_REJECT') {
      return `Reject ${applicationTypeTitle}`;
    }
    return `Send Notification for ${applicationTypeTitle}`;
  }, [
    creditApplication,
    mode,
    hasErpCustomerIntegration,
    customerOnApprovalAction,
    applicationTypeTitle,
  ]);

  const message = useMemo(() => {
    const prefix =
      hasErpCustomerIntegration && customerOnApprovalAction !== 'none' ? '' : 'Internally ';
    if (mode === 'INTERNALLY_REJECT') {
      return `reject ${applicationTypeName}`;
    }
    return mode === 'INTERNALLY_APPROVE' && isCreditApp
      ? `${prefix}approve ${applicationTypeName}`
      : mode === 'INTERNALLY_APPROVE' && creditApplication.applicationType === 'CASH_APPLICATION'
      ? `${prefix}approve COD`
      : `${prefix}reject ${applicationTypeName} and approve COD`;
  }, [
    creditApplication,
    mode,
    hasErpCustomerIntegration,
    customerOnApprovalAction,
    applicationTypeName,
  ]);

  const renderEmailTagList = (
    label: string,
    emails: string[],
    setEmails: (v: string[]) => void,
    inputValue: string,
    setInputValue: (v: string) => void,
    inputError: string,
    setInputError: (v: string) => void
  ) => (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2">
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {emails.map((email, idx) => (
              <span
                key={`${idx}o`}
                className="inline-flex items-center gap-1 text-xs bg-white border border-blue-200 text-blue-700 rounded-md px-2 py-1">
                {email}
                <button
                  className="text-blue-400 hover:text-red-500 cursor-pointer bg-transparent border-none p-0 leading-none"
                  onClick={() => setEmails(emails.filter((_, i) => i !== idx))}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <DillInput
              value={inputValue}
              type="email"
              placeholder="Add email address"
              error={inputError}
              onChange={(text) => {
                setInputError('');
                setInputValue(text);
              }}
            />
            {inputError && <p className="text-2xs mt-0.5 text-red-500">{inputError}</p>}
          </div>
          <DillButton
            variant="icon-blue"
            icon={<Plus size={16} />}
            onClick={() => {
              EMAIL_REGEX.lastIndex = 0;
              if (EMAIL_REGEX.test(inputValue)) {
                setEmails(dedupeEmails([...emails, inputValue]));
                setInputValue('');
                setInputError('');
              } else {
                setInputError('Invalid email');
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const addEmail = (emails: string[], onSuccess: (updated: string[]) => void) => {
    if (!emailTextInputValue) return onSuccess(emails);
    EMAIL_REGEX.lastIndex = 0;
    if (EMAIL_REGEX.test(emailTextInputValue)) {
      const updated = dedupeEmails([...emails, emailTextInputValue]);
      setCreditAppEmails(updated);
      setEmailTextInputValue('');
      setEmailTextInputErrorMessage('');
      onSuccess(updated);
    } else {
      setEmailTextInputErrorMessage('Invalid email');
    }
  };

  const commitEmailInput = (
    inputValue: string,
    emails: string[],
    setEmails: (v: string[]) => void,
    setInputValue: (v: string) => void,
    setInputError: (v: string) => void
  ): string[] | null => {
    if (!inputValue.trim()) return emails;
    EMAIL_REGEX.lastIndex = 0;
    if (EMAIL_REGEX.test(inputValue)) {
      const updated = dedupeEmails([...emails, inputValue]);
      setEmails(updated);
      setInputValue('');
      setInputError('');
      return updated;
    }
    setInputError('Invalid email');
    return null;
  };

  const emailTagList = (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-gray-500">
        {`Email${creditAppEmails.length > 1 ? 's' : ''} to notify${
          mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD'
            ? hasErpCustomerIntegration && customerOnApprovalAction !== 'none'
              ? ` on approval`
              : ` on approval (internally)`
            : mode === 'INTERNALLY_REJECT'
            ? ` on rejection`
            : ''
        }`}
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2">
        {creditAppEmails.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {creditAppEmails.map((email, idx) => (
              <span
                key={`${idx}o`}
                className="inline-flex items-center gap-1 text-xs bg-white border border-blue-200 text-blue-700 rounded-md px-2 py-1">
                {email}
                <button
                  className="text-blue-400 hover:text-red-500 cursor-pointer bg-transparent border-none p-0 leading-none"
                  onClick={() => setCreditAppEmails(creditAppEmails.filter((_, i) => i !== idx))}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <DillInput
              value={emailTextInputValue}
              type="email"
              placeholder="Add email address"
              error={emailTextInputErrorMessage}
              onChange={(text) => {
                setEmailTextInputErrorMessage('');
                setEmailTextInputValue(text);
                setMissingEmails(false);
              }}
            />
            {emailTextInputErrorMessage && (
              <p className="text-2xs mt-0.5 text-red-500">{emailTextInputErrorMessage}</p>
            )}
          </div>
          <DillButton
            variant="icon-blue"
            icon={<Plus size={16} />}
            onClick={() => {
              EMAIL_REGEX.lastIndex = 0;
              if (EMAIL_REGEX.test(emailTextInputValue)) {
                setCreditAppEmails(dedupeEmails([...creditAppEmails, emailTextInputValue]));
                setEmailTextInputValue('');
                setEmailTextInputErrorMessage('');
              } else {
                setEmailTextInputErrorMessage('Invalid email');
              }
            }}
          />
        </div>
      </div>
      {missingEmails && (
        <p className="text-xs text-red-500 text-center">
          Please add an email to send the credit application.
        </p>
      )}
    </div>
  );

  const oneOffOverrideBlock = (
    <div className="border rounded p-2 mt-2">
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={useOneOffOverride}
          onChange={(e) => {
            const checked = e.target.checked;
            setUseOneOffOverride(checked);
            if (checked) {
              setOverrideSubject((prev) => prev || templateOverrideDefaults.subject);
              setOverrideMessageHtml((prev) => {
                const seeded = prev || templateOverrideDefaults.message;
                setOverrideMessageSeed(seeded);
                return seeded;
              });
            }
          }}
          className="w-4 h-4"
        />
        <span className="text-xs font-semibold">
          Override message template for this application only
        </span>
      </label>
      {useOneOffOverride && (
        <div className="flex flex-col gap-2">
          <AppInputField
            label="Subject Line"
            value={overrideSubject}
            onTextChange={setOverrideSubject}
          />
          <LexicalEditor
            label="Message"
            outputFormat="html"
            initialContent={overrideMessageSeed}
            onChange={(html) => {
              setOverrideMessageHtml(html);
            }}
            placeholder="Write the message for this application..."
          />
        </div>
      )}
    </div>
  );

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title={title}
      maxWidth="640px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          {(mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD') && (
            <DillButton
              label="Approve"
              variant="primary"
              isLoading={loading}
              isDisabled={loading}
              onClick={() => {
                if (mode === 'INTERNALLY_APPROVE' && isCreditApp && !resolvedTerms) {
                  setMissingTerms(true);
                  return;
                }
                if (
                  hasErpCustomerIntegration &&
                  (customerOnApprovalAction === 'update' || showDmsiAccountNumberInput) &&
                  (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD') &&
                  !customerAccountNumber.trim()
                ) {
                  setMissingCustomerAccountNumber(true);
                  return;
                }
                if (showEclipseBillToInput && !billToAccountNumber.trim()) {
                  setMissingBillToAccountNumber(true);
                  return;
                }
                if (showNetsuiteSubsidiaryDropdown && !selectedSubsidiaryId) {
                  setMissingSubsidiary(true);
                  return;
                }
                if (showSplitEmailLists) {
                  const updatedInternalEmails = commitEmailInput(
                    internalEmailInput,
                    internalEmailList,
                    setInternalEmailList,
                    setInternalEmailInput,
                    setInternalEmailInputError
                  );
                  if (updatedInternalEmails === null) return;
                  const updatedCustomerEmails = commitEmailInput(
                    customerEmailInput,
                    customerEmailList,
                    setCustomerEmailList,
                    setCustomerEmailInput,
                    setCustomerEmailInputError
                  );
                  if (updatedCustomerEmails === null) return;
                  handleInternalAprove(updatedInternalEmails, updatedCustomerEmails);
                } else {
                  addEmail(creditAppEmails, handleInternalAprove);
                }
              }}
            />
          )}
          {mode === 'INTERNALLY_REJECT' && (
            <DillButton
              label="Reject"
              variant="danger"
              isLoading={loading}
              isDisabled={loading}
              onClick={() => addEmail(creditAppEmails, handleInternalReject)}
            />
          )}
          {mode === 'NOTIFY' && (
            <DillButton
              label="Notify"
              variant="primary"
              isLoading={loading}
              isDisabled={loading}
              onClick={() => addEmail(creditAppEmails, handleAprove)}
            />
          )}
        </>
      }>
      {(mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD') && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500 ">
            {`${message.charAt(0).toUpperCase()}${message.slice(1)}.${
              hasErpCustomerIntegration && customerOnApprovalAction !== 'none'
                ? ' The applicant will be notified immediately.'
                : ` You will notify the ${applicationTypeName} sender in the following step.`
            }`}
          </p>

          <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
            <p className="text-sm font-semibold text-blue-600">
              {mode === 'INTERNALLY_APPROVE_COD' && isCreditApp
                ? `Reject ${getCreditApplicationTypeLabel(creditApplication.applicationType, {
                    formName: notifyTemplate?.name,
                  })} and Approve COD`
                : creditApplication.applicationType === 'CASH_APPLICATION'
                ? 'Approve COD'
                : 'Approve Application'}
            </p>
            {eclipseShipToCreated && (
              <p className="text-xs text-gray-600 m-0">
                {`Already in Eclipse: Bill-To ${
                  creditApplication.customerAccountNumber || '—'
                }, Ship-To ${
                  creditApplication.shipToAccountNumber
                } (COD). Approving sets the Ship-To's terms and credit limit.`}
              </p>
            )}
            {canOverrideCustomerAction && !eclipseShipToCreated && (
              <DillDropdown
                containerClassName="h-[40px] p-1"
                label="Customer action on approval"
                value={customerOnApprovalAction}
                items={
                  isEclipseFormActionFixed
                    ? [{ value: eclipseFormAction, label: eclipseFormActionLabel }]
                    : [
                        { value: 'update', label: 'Update customer' },
                        { value: 'create', label: 'Create customer' },
                      ]
                }
                onSelectChange={(item) => {
                  const nextAction = item.value as 'update' | 'create';
                  setCustomerActionOverride(
                    nextAction === defaultCustomerOnApprovalAction ? null : nextAction
                  );

                  if (
                    nextAction !== 'update' &&
                    !((isDMSI || isInforSX) && nextAction === 'create')
                  ) {
                    setCustomerAccountNumber('');
                  }
                  setMissingCustomerAccountNumber(false);
                }}
              />
            )}
            {canOverridePreserveAccountDetails && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  Customer Type
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preserveExistingAccountDetails"
                      value="new"
                      checked={!preserveExistingAccountDetails}
                      onChange={() => {
                        setPreserveAccountDetailsOverride(
                          defaultPreserveExistingAccountDetails === false ? null : false
                        );
                      }}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold">New Customer</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preserveExistingAccountDetails"
                      value="existing"
                      checked={preserveExistingAccountDetails}
                      onChange={() => {
                        setPreserveAccountDetailsOverride(
                          defaultPreserveExistingAccountDetails === true ? null : true
                        );
                      }}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold">Existing Customer</span>
                  </label>
                </div>
              </div>
            )}
            {mode !== 'INTERNALLY_APPROVE_COD' && isCreditApp && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="flex-1 bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-0.5">Amount Requested</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatMoney(amountOfCreditRequested, 0)}
                    </p>
                  </div>
                  <div className="flex-1">
                    <AppInputField
                      label="Amount Approved"
                      value={amountApproved ? formatWithCommas(String(amountApproved)) : ''}
                      inputType="text"
                      prefix="$"
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value.replace(/[^0-9.]/g, ''));
                        if (!val || val < 0) setAmountApproved(amountOfCreditRequested);
                      }}
                      onTextChange={(text) => {
                        const numeric = parseFloat(text.replace(/[^0-9.]/g, '')) || 0;
                        setAmountApproved(numeric);
                      }}
                    />
                  </div>
                </div>
                {supplierTerms.length > 0 ? (
                  <>
                    <DillDropdown
                      containerClassName="h-[40px] p-1"
                      label="Terms"
                      isRequired
                      placeholder={loadingPaymentTerms ? 'Loading terms...' : 'Select terms...'}
                      value={terms || null}
                      errorText={missingTerms ? 'Please select terms before approving.' : undefined}
                      items={[
                        ...supplierTerms.map((t: { name: string; erpId?: string }) => ({
                          value: t.name,
                          label: t.name,
                        })),
                      ]}
                      onSelectChange={(item) => {
                        setTerms(item.value);
                        setMissingTerms(false);
                      }}
                    />
                  </>
                ) : (
                  <AppInputField
                    value={terms}
                    label="Terms"
                    isRequired
                    placeholder="eg. Net 30"
                    errorText={missingTerms ? 'Please enter terms before approving.' : undefined}
                    onTextChange={(text) => {
                      setTerms(String(text));
                      setMissingTerms(false);
                    }}
                    onBlur={(e) => {
                      if (e.target.value) setTerms(e.target.value);
                    }}
                  />
                )}
                {isEclipseShipToFollowingBillToTerms && (
                  <p className="text-xs ml-1 text-left GREY_500-CLR">
                    The new Ship-To in Eclipse takes the Bill-To&apos;s terms, not these.
                  </p>
                )}
              </div>
            )}
            {(notifyTemplate?.approvalAdditionalFields ?? []).length > 0 && (
              <div className="flex flex-col gap-3">
                {(notifyTemplate?.approvalAdditionalFields ?? []).map((field) => {
                  const entry = approvalAdditionalFieldValues.find((e) => e.id === field.id);
                  const value = entry?.value ?? field.defaultValue ?? '';
                  const isCustom = value === '__custom__';
                  const updateEntry = (val: string) =>
                    setApprovalAdditionalFieldValues((prev) => {
                      const exists = prev.find((e) => e.id === field.id);
                      if (exists) {
                        return prev.map((e) => (e.id === field.id ? { ...e, value: val } : e));
                      }
                      return [
                        ...prev,
                        { id: field.id, name: field.label, erpId: field.erpId, value: val },
                      ];
                    });
                  return (
                    <div key={field.id} className="flex flex-col gap-1">
                      {field.type === 'dropdown' ? (
                        <>
                          <DillDropdown
                            containerClassName="h-[40px] p-1"
                            label={field.label}
                            isRequired={field.required}
                            placeholder="Select..."
                            value={isCustom ? '__custom__' : value || null}
                            items={[
                              ...getApprovalFieldOptions(field, supplierBranches).map((opt) => {
                                const isObj = typeof opt === 'object' && opt !== null;
                                return {
                                  value: isObj ? (opt as { name: string }).name : opt,
                                  label: isObj ? (opt as { name: string }).name : opt,
                                };
                              }),
                              ...(field.allowCustomOption
                                ? [{ value: '__custom__', label: 'Other' }]
                                : []),
                            ]}
                            onSelectChange={(item) => {
                              const selectedOpt = getApprovalFieldOptions(
                                field,
                                supplierBranches
                              ).find((opt) => {
                                const isObj = typeof opt === 'object' && opt !== null;
                                return isObj
                                  ? (opt as { name: string }).name === item.value
                                  : opt === item.value;
                              });
                              const selectedErpId =
                                selectedOpt && typeof selectedOpt === 'object'
                                  ? (selectedOpt as { erpId?: string }).erpId
                                  : undefined;
                              setApprovalAdditionalFieldValues((prev) => {
                                const exists = prev.find((e) => e.id === field.id);
                                if (exists) {
                                  return prev.map((e) =>
                                    e.id === field.id
                                      ? { ...e, value: item.value, erpId: selectedErpId ?? e.erpId }
                                      : e
                                  );
                                }
                                return [
                                  ...prev,
                                  {
                                    id: field.id,
                                    name: field.label,
                                    erpId: selectedErpId ?? field.erpId,
                                    value: item.value,
                                  },
                                ];
                              });
                            }}
                          />
                          {field.allowCustomOption && isCustom && (
                            <AppInputField
                              label=""
                              value={customOptionValues[field.id] ?? ''}
                              placeholder="Specify..."
                              onTextChange={(text) =>
                                setCustomOptionValues((prev) => ({
                                  ...prev,
                                  [field.id]: text,
                                }))
                              }
                            />
                          )}
                        </>
                      ) : field.type === 'currency' ? (
                        <AppInputField
                          label={`${field.label}${field.required ? ' *' : ''}`}
                          value={
                            currencyDisplayValues[field.id] ??
                            (value ? formatWithCommas(String(value)) : '')
                          }
                          inputType="text"
                          prefix="$"
                          onTextChange={(text) => {
                            const formatted = formatWithCommas(String(text));
                            setCurrencyDisplayValues((prev) => ({
                              ...prev,
                              [field.id]: formatted,
                            }));
                            const numeric = String(text).replace(/[^0-9.]/g, '');
                            updateEntry(numeric);
                          }}
                        />
                      ) : (
                        <AppInputField
                          label={`${field.label}${field.required ? ' *' : ''}`}
                          value={value}
                          inputType={field.type === 'number' ? 'number' : 'text'}
                          onTextChange={(text) => updateEntry(String(text))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showNetsuiteSubsidiaryDropdown && (
              <AppDropDown2
                isRequired
                label="Subsidiary in NetSuite"
                isLoading={loadingSubsidiaries}
                placeholder={
                  loadingSubsidiaries && subsidiaries.length === 0
                    ? 'Loading subsidiaries...'
                    : 'Select subsidiary to create customer under'
                }
                errorText={
                  missingSubsidiary ? 'Please select the subsidiary before approving.' : undefined
                }
                value={selectedSubsidiaryId}
                items={subsidiaries.map((subsidiary) => ({
                  label: subsidiary.name ?? '',
                  value: subsidiary.id ?? '',
                  labelInElement: (
                    <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                      <div className="text-sm">{subsidiary.name}</div>
                    </div>
                  ),
                }))}
                onSelectChange={(item) => {
                  if (!item || item.value === null) {
                    return;
                  }
                  setSelectedSubsidiaryId(String(item.value));
                  setMissingSubsidiary(false);
                }}
              />
            )}
            {mode === 'INTERNALLY_APPROVE_COD' && isCreditApp && activeRejectReasons.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-gray-500"> Approve COD reason (Optional)</p>
                <div className="flex flex-col gap-1.5">
                  {activeRejectReasons.map((rejectReason: string) => (
                    <label
                      key={rejectReason}
                      className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 cursor-pointer"
                        checked={selectedRejectReasons.has(rejectReason)}
                        onChange={(e) => {
                          setSelectedRejectReasons((prev) => {
                            const updated = new Set(prev);
                            if (e.target.checked) updated.add(rejectReason);
                            else updated.delete(rejectReason);
                            return updated;
                          });
                        }}
                      />
                      <span className="text-xs text-gray-700">{rejectReason}</span>
                    </label>
                  ))}
                </div>
                {selectedRejectReasons.has('Other') && (
                  <AppInputField
                    label="Other reason"
                    value={otherReasonText}
                    placeholder="Please specify"
                    onTextChange={(val) => setOtherReasonText(val)}
                  />
                )}
              </div>
            )}
            {showEclipseBillToInput && (
              <AppInputField
                value={billToAccountNumber}
                label="Bill-To Number in Eclipse"
                isRequired
                errorText={
                  missingBillToAccountNumber
                    ? 'Please enter the Bill-To number before approving.'
                    : undefined
                }
                placeholder="Enter the existing Bill-To number"
                onTextChange={(text) => {
                  setBillToAccountNumber(text);
                  setMissingBillToAccountNumber(false);
                }}
              />
            )}
            {/* Show customer number input when action is 'update' */}
            {hasErpCustomerIntegration &&
              customerOnApprovalAction === 'update' &&
              (mode === 'INTERNALLY_APPROVE' || mode === 'INTERNALLY_APPROVE_COD') && (
                <AppInputField
                  value={customerAccountNumber}
                  label={
                    isEclipse
                      ? 'Ship-To Number in Eclipse'
                      : `Customer Number in ${getIntegrationName(currentSupplier?.integrationType)}`
                  }
                  isRequired
                  errorText={
                    missingCustomerAccountNumber
                      ? 'Please enter the customer number before approving.'
                      : undefined
                  }
                  placeholder="Enter existing customer number to update"
                  onTextChange={(text) => {
                    setCustomerAccountNumber(text);
                    setMissingCustomerAccountNumber(false);
                  }}
                  onBlur={(e) => {
                    if (e.target.value) setCustomerAccountNumber(e.target.value);
                  }}
                />
              )}
            {showDmsiAccountNumberInput && (
              <AppInputField
                value={customerAccountNumber}
                label="Account Number in Agility DMSi"
                isRequired
                errorText={
                  missingCustomerAccountNumber
                    ? 'Please enter an account number before approving.'
                    : undefined
                }
                placeholder="Enter the account number to assign to the new customer"
                onTextChange={(text) => {
                  setCustomerAccountNumber(text);
                  setMissingCustomerAccountNumber(false);
                }}
                onBlur={(e) => {
                  if (e.target.value) setCustomerAccountNumber(e.target.value);
                }}
              />
            )}
            {showSplitEmailLists ? (
              <>
                {renderEmailTagList(
                  'Internal emails to notify on approval',
                  internalEmailList,
                  setInternalEmailList,
                  internalEmailInput,
                  setInternalEmailInput,
                  internalEmailInputError,
                  setInternalEmailInputError
                )}
                {renderEmailTagList(
                  'Emails to notify on customer approval',
                  customerEmailList,
                  setCustomerEmailList,
                  customerEmailInput,
                  setCustomerEmailInput,
                  customerEmailInputError,
                  setCustomerEmailInputError
                )}
              </>
            ) : (
              emailTagList
            )}
            {mode === 'INTERNALLY_APPROVE' && (
              <AppInputField
                label="Additional Message (Optional)"
                value={reason}
                inputType="textarea"
                placeholder="Enter any additional message"
                onTextChange={(val) => setReason(val)}
              />
            )}
            {mode === 'INTERNALLY_APPROVE_COD' &&
              isCreditApp &&
              activeRejectReasons.length === 0 && (
                <AppInputField
                  label="Approve COD reason (Optional)"
                  value={reason}
                  inputType="textarea"
                  placeholder="Enter reason for approving COD"
                  onTextChange={(val) => setReason(val)}
                />
              )}
            {hasErpCustomerIntegration &&
              customerOnApprovalAction !== 'none' &&
              oneOffOverrideBlock}
          </div>
        </div>
      )}

      {mode === 'INTERNALLY_REJECT' && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500">
            {`${message.charAt(0).toUpperCase()}${message.slice(
              1
            )}. The sender will be notified immediately.`}
          </p>

          <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
            <p className="text-sm font-semibold text-red-600">Reject Application</p>

            <AppInputField
              label="Rejection Reason (Optional)"
              value={reason}
              inputType="textarea"
              placeholder="Enter reason for rejection"
              onTextChange={(val) => setReason(val)}
            />

            {emailTagList}

            {oneOffOverrideBlock}
          </div>
        </div>
      )}

      {mode === 'NOTIFY' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm font-semibold text-blue-600">{status.replaceAll('_', ' ')}</p>

          {creditApplication.status === 'INTERNALLY_APPROVED' && (
            <div className="bg-gray-50 rounded-lg p-3 flex w-full justify-between items-center">
              {isCreditApp && (
                <div className="w-full">
                  <p className="text-xs text-gray-400 mb-0.5">Amount Approved</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {formatMoney(amountOfCreditApproved, 0)}
                  </p>
                </div>
              )}
              {creditApplication.terms && (
                <div className="w-full">
                  <p className="text-xs text-gray-400 mb-0.5">Terms</p>
                  <p className="text-sm font-semibold text-gray-800">{creditApplication.terms}</p>
                </div>
              )}
            </div>
          )}

          {emailTagList}

          {creditApplication.status !== 'INTERNALLY_REJECTED' &&
            !(
              skipCustomerActionOnCod && creditApplication.status === 'INTERNALLY_APPROVED_COD'
            ) && (
              <AppInputField
                value={customerAccountNumber}
                label="Customer Account Number"
                placeholder="Add customer account number"
                onTextChange={(text) => setCustomerAccountNumber(text)}
                onBlur={(e) => {
                  if (e.target.value) setCustomerAccountNumber(e.target.value);
                }}
              />
            )}

          {creditApplication.rejectReason && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-gray-500">Reason</p>
              <div
                className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200"
                dangerouslySetInnerHTML={{ __html: creditApplication.rejectReason }}
              />
            </div>
          )}

          {creditApplication.oneOffMessageOverrideHtml && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-medium text-gray-500">Custom Message Used</p>
              <div
                className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200"
                dangerouslySetInnerHTML={{ __html: creditApplication.oneOffMessageOverrideHtml }}
              />
            </div>
          )}

          {oneOffOverrideBlock}
        </div>
      )}
    </DillModal>
  );
};

export default ApproveCreditAppEmailsModal;
